import { APP_API_URL } from "@/consts";
import graphqlClient from "@/GQLClient";
import type { GetEmployeeStatisticsEmployeeOnly, LoggedUser } from "@/graphql";
import {
  DeleteUserDocument,
  GetByEmailOrUsernameDocument,
  GetSelfQueryDocument,
  GetEmployeeStatisticsDocument,
  SaveUserDocument,
  type UserSaveInput,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";

const getToken = async (jwt: string) => {
  const response = await window.fetch(`${APP_API_URL}/user/accesstoken`, {
    method: "GET",
    headers: {
      Authorization: jwt,
    },
  });

  if (response.status === 200) {
    return await response.json();
  }

  throw new Error(`Auth token error.`);
};

const getSelf = async (): Promise<LoggedUser> => {
  const response = await graphqlClient.query({
    query: GetSelfQueryDocument,
    variables: {},
  });

  if (response.data.self) {
    return response.data.self;
  }

  throw new SentryGraphQLError();
};

const save = async (user: UserSaveInput) => {
  const response = await graphqlClient.mutate({
    mutation: SaveUserDocument,
    variables: {
      user,
    },
  });

  if (response.data?.user?.saveUser?.success) {
    return response.data.user.saveUser.id;
  }

  throw new SentryGraphQLError();
};

const deleteItem = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteUserDocument,
    variables: {
      id,
    },
  });

  if (response.data?.user?.deleteUser?.success) {
    return response.data.user.deleteUser.success;
  }

  throw new SentryGraphQLError();
};

const getUsersStatistics = async (
  ids: string[],
): Promise<GetEmployeeStatisticsEmployeeOnly[]> => {
  const response = await graphqlClient.query({
    query: GetEmployeeStatisticsDocument,
    variables: {
      filter: {
        ids,
      },
    },
  });

  if (response.data.self?.institutions) {
    return response.data.self.institutions
      .flatMap((institution) =>
        (institution.employees?.edges ?? []).map((employee) => employee.node),
      )
      .filter(
        (node): node is GetEmployeeStatisticsEmployeeOnly =>
          node.__typename === "Employee",
      );
  }

  throw new SentryGraphQLError();
};

const getUserStatistics = async (id: string) => {
  const response = await getUsersStatistics([id]);
  const [user] = response;
  if (user) {
    return user;
  }

  throw new Error("GetUserStatistics query error.");
};

const getIdByEmailInUse = async (email: string) => {
  const response = await graphqlClient.query({
    query: GetByEmailOrUsernameDocument,

    variables: {
      email,
      username: email,
    },
  });

  return response.data.user?.getByEmailOrUsername?.id;
};

export default {
  getToken,
  getSelf,
  save,
  deleteItem,
  getUsersStatistics,
  getUserStatistics,
  getIdByEmailInUse,
};
